

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Loader2 } from "lucide-react";
import { Toast, ToastPosition, ToastVariant, useToast } from "src/lib/toast-context";
import { cn } from "src/lib/utils";

const variantStyles: Record<ToastVariant, { bg: string; border: string; icon: React.ReactNode }> = {
  default: {
    bg: "bg-white dark:bg-gray-900",
    border: "border-gray-200 dark:border-gray-700",
    icon: <Info className="w-5 h-5 text-gray-600 dark:text-gray-400" />,
  },
  success: {
    bg: "bg-emerald-50 dark:bg-emerald-950",
    border: "border-emerald-200 dark:border-emerald-800",
    icon: <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />,
  },
  error: {
    bg: "bg-red-50 dark:bg-red-950",
    border: "border-red-200 dark:border-red-800",
    icon: <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />,
  },
  warning: {
    bg: "bg-amber-50 dark:bg-amber-950",
    border: "border-amber-200 dark:border-amber-800",
    icon: <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />,
  },
  info: {
    bg: "bg-blue-50 dark:bg-blue-950",
    border: "border-blue-200 dark:border-blue-800",
    icon: <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
  },
  loading: {
    bg: "bg-white dark:bg-gray-900",
    border: "border-gray-200 dark:border-gray-700",
    icon: <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />,
  },
};

const positionStyles: Record<ToastPosition, string> = {
  "top-center": "top-6",
  "bottom-center": "bottom-6",
  "center": "top-1/2 -translate-y-1/2",
};

function ToastItem({ toast }: { toast: Toast }) {
  const { dismissToast } = useToast();
  const variant = toast.variant || "default";
  const position = toast.position || "top-center";
  const styles = variantStyles[variant];

  const isTop = position === "top-center";
  const isCenter = position === "center";

  const getInitialY = () => {
    if (isCenter) return 0;
    return isTop ? -100 : 100;
  };

  const getExitY = () => {
    if (isCenter) return 0;
    return isTop ? -20 : 20;
  };

  return (
    <motion.div
      layout
      initial={{
        opacity: 0,
        y: getInitialY(),
        scale: isCenter ? 0.9 : 0.8,
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        scale: isCenter ? 0.9 : 0.8,
        y: getExitY(),
        transition: {
          duration: 0.2,
        },
      }}
      transition={{
        type: "spring",
        stiffness: 350,
        damping: 25,
      }}
      className={cn(
        "relative flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg backdrop-blur-sm",
        "min-w-[320px] max-w-md",
        styles.bg,
        styles.border,
        isCenter && "shadow-2xl"
      )}
    >
      <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>

      <div className="flex-1 pt-0.5">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-relaxed">
          {toast.message}
        </p>
      </div>

      {variant !== "loading" && (
        <button
          onClick={() => dismissToast(toast.id)}
          className="flex-shrink-0 rounded-lg p-1 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </button>
      )}
    </motion.div>
  );
}

function PromiseToastItem({ toast }: { toast: Toast }) {
  const variant = toast.variant || "loading";
  const styles = variantStyles[variant];

  return (
    <motion.div
      layout
      initial={{
        opacity: 0,
        scale: 0.9,
      }}
      animate={{
        opacity: 1,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        scale: 0.9,
        transition: {
          duration: 0.2,
        },
      }}
      transition={{
        type: "spring",
        stiffness: 350,
        damping: 25,
      }}
      className={cn(
        "relative flex items-start gap-3 rounded-xl border px-5 py-4 shadow-2xl backdrop-blur-md",
        "min-w-[340px] max-w-md",
        styles.bg,
        styles.border
      )}
    >
      <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>

      <div className="flex-1 pt-0.5">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-relaxed">
          {toast.message}
        </p>
      </div>
    </motion.div>
  );
}

export function CustomToastContainer() {
  const { toasts } = useToast();

  const topToasts = toasts.filter((t) => t.position === "top-center");
  const bottomToasts = toasts.filter((t) => t.position === "bottom-center");
  const centerToasts = toasts.filter((t) => t.position === "center");

  return (
    <>
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
        <div className="flex flex-col gap-3 pointer-events-auto">
          <AnimatePresence mode="popLayout">
            {topToasts.map((toast) => (
              <ToastItem key={toast.id} toast={toast} />
            ))}
          </AnimatePresence>
        </div>
      </div>

      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
        <div className="flex flex-col-reverse gap-3 pointer-events-auto">
          <AnimatePresence mode="popLayout">
            {bottomToasts.map((toast) => (
              <ToastItem key={toast.id} toast={toast} />
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {centerToasts.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <div className="flex flex-col gap-3">
              <AnimatePresence mode="sync">
                {centerToasts.map((toast) => (
                  <PromiseToastItem key={toast.id} toast={toast} />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


// "use client";

// import { useToast } from "@/lib/toast-context";
// import { Button } from "@/components/ui/button";
// import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

// export default function Home() {
//   const { showToast, promiseToast } = useToast();

//   const simulateAPICall = (shouldFail = false, delay = 2000): Promise<{ data: string }> => {
//     return new Promise((resolve, reject) => {
//       setTimeout(() => {
//         if (shouldFail) {
//           reject(new Error("API request failed"));
//         } else {
//           resolve({ data: "User data loaded successfully" });
//         }
//       }, delay);
//     });
//   };

//   return (
//     <main className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
//       <div className="container mx-auto px-4 py-16">
//         <div className="max-w-4xl mx-auto space-y-8">
//           <div className="text-center space-y-4">
//             <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
//               Custom Toast Notifications
//             </h1>
//             <p className="text-lg text-gray-600 dark:text-gray-400">
//               Professional toast notifications with smooth Framer Motion animations
//             </p>
//           </div>

//           <div className="grid gap-6 md:grid-cols-2">
//             <Card className="border-2 shadow-lg">
//               <CardHeader>
//                 <CardTitle>Top Center Position</CardTitle>
//                 <CardDescription>
//                   Toast notifications appear at the top center of the screen
//                 </CardDescription>
//               </CardHeader>
//               <CardContent className="space-y-3">
//                 <Button
//                   onClick={() =>
//                     showToast("This is a default notification", {
//                       position: "top-center",
//                       variant: "default",
//                     })
//                   }
//                   variant="outline"
//                   className="w-full"
//                 >
//                   Default Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("Operation completed successfully!", {
//                       position: "top-center",
//                       variant: "success",
//                     })
//                   }
//                   className="w-full bg-emerald-600 hover:bg-emerald-700"
//                 >
//                   Success Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("An error occurred. Please try again.", {
//                       position: "top-center",
//                       variant: "error",
//                     })
//                   }
//                   className="w-full bg-red-600 hover:bg-red-700"
//                 >
//                   Error Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("Warning: This action cannot be undone", {
//                       position: "top-center",
//                       variant: "warning",
//                     })
//                   }
//                   className="w-full bg-amber-600 hover:bg-amber-700"
//                 >
//                   Warning Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("Here's some helpful information for you", {
//                       position: "top-center",
//                       variant: "info",
//                     })
//                   }
//                   className="w-full bg-blue-600 hover:bg-blue-700"
//                 >
//                   Info Toast
//                 </Button>
//               </CardContent>
//             </Card>

//             <Card className="border-2 shadow-lg">
//               <CardHeader>
//                 <CardTitle>Bottom Center Position</CardTitle>
//                 <CardDescription>
//                   Toast notifications appear at the bottom center of the screen
//                 </CardDescription>
//               </CardHeader>
//               <CardContent className="space-y-3">
//                 <Button
//                   onClick={() =>
//                     showToast("This is a default notification", {
//                       position: "bottom-center",
//                       variant: "default",
//                     })
//                   }
//                   variant="outline"
//                   className="w-full"
//                 >
//                   Default Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("Changes saved successfully!", {
//                       position: "bottom-center",
//                       variant: "success",
//                     })
//                   }
//                   className="w-full bg-emerald-600 hover:bg-emerald-700"
//                 >
//                   Success Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("Failed to load data. Check your connection.", {
//                       position: "bottom-center",
//                       variant: "error",
//                     })
//                   }
//                   className="w-full bg-red-600 hover:bg-red-700"
//                 >
//                   Error Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("Your session will expire in 5 minutes", {
//                       position: "bottom-center",
//                       variant: "warning",
//                     })
//                   }
//                   className="w-full bg-amber-600 hover:bg-amber-700"
//                 >
//                   Warning Toast
//                 </Button>
//                 <Button
//                   onClick={() =>
//                     showToast("Pro tip: Use keyboard shortcuts for faster navigation", {
//                       position: "bottom-center",
//                       variant: "info",
//                     })
//                   }
//                   className="w-full bg-blue-600 hover:bg-blue-700"
//                 >
//                   Info Toast
//                 </Button>
//               </CardContent>
//             </Card>
//           </div>

//           <Card className="border-2 shadow-lg bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
//             <CardHeader>
//               <CardTitle>Custom Duration</CardTitle>
//               <CardDescription>
//                 Control how long the toast stays visible
//               </CardDescription>
//             </CardHeader>
//             <CardContent className="flex flex-wrap gap-3">
//               <Button
//                 onClick={() =>
//                   showToast("Quick message (1 second)", {
//                     position: "top-center",
//                     variant: "info",
//                     duration: 1000,
//                   })
//                 }
//                 variant="secondary"
//               >
//                 1 Second
//               </Button>
//               <Button
//                 onClick={() =>
//                   showToast("Standard message (3 seconds)", {
//                     position: "top-center",
//                     variant: "success",
//                     duration: 3000,
//                   })
//                 }
//                 variant="secondary"
//               >
//                 3 Seconds
//               </Button>
//               <Button
//                 onClick={() =>
//                   showToast("Longer message (5 seconds)", {
//                     position: "bottom-center",
//                     variant: "warning",
//                     duration: 5000,
//                   })
//                 }
//                 variant="secondary"
//               >
//                 5 Seconds
//               </Button>
//               <Button
//                 onClick={() =>
//                   showToast("Extended message (10 seconds)", {
//                     position: "bottom-center",
//                     variant: "error",
//                     duration: 10000,
//                   })
//                 }
//                 variant="secondary"
//               >
//                 10 Seconds
//               </Button>
//             </CardContent>
//           </Card>

//           <Card className="border-2 shadow-lg border-dashed">
//             <CardHeader>
//               <CardTitle>Multiple Toasts</CardTitle>
//               <CardDescription>
//                 Test stacking behavior with multiple notifications
//               </CardDescription>
//             </CardHeader>
//             <CardContent>
//               <Button
//                 onClick={() => {
//                   showToast("First notification", { position: "top-center", variant: "info" });
//                   setTimeout(() => {
//                     showToast("Second notification", { position: "top-center", variant: "success" });
//                   }, 300);
//                   setTimeout(() => {
//                     showToast("Third notification", { position: "top-center", variant: "warning" });
//                   }, 600);
//                 }}
//                 className="w-full"
//                 size="lg"
//               >
//                 Show Multiple Toasts
//               </Button>
//             </CardContent>
//           </Card>

//           <Card className="border-2 shadow-lg bg-gradient-to-br from-blue-50/50 to-white dark:from-blue-950/20 dark:to-gray-950">
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 Promise Toast - API Calls
//                 <span className="text-xs font-normal bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
//                   Center Position
//                 </span>
//               </CardTitle>
//               <CardDescription>
//                 Loading state with success or error transition for async operations
//               </CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-3">
//               <Button
//                 onClick={async () => {
//                   try {
//                     await promiseToast(simulateAPICall(false, 2000), {
//                       loading: "Loading user data...",
//                       success: "User data loaded successfully!",
//                       error: "Failed to load user data",
//                     });
//                   } catch (error) {
//                     console.error(error);
//                   }
//                 }}
//                 className="w-full bg-emerald-600 hover:bg-emerald-700"
//               >
//                 Simulate Successful API Call
//               </Button>
//               <Button
//                 onClick={async () => {
//                   try {
//                     await promiseToast(simulateAPICall(true, 2000), {
//                       loading: "Fetching data from server...",
//                       success: "Data fetched successfully!",
//                       error: "Network error occurred",
//                     });
//                   } catch (error) {
//                     console.error(error);
//                   }
//                 }}
//                 className="w-full bg-red-600 hover:bg-red-700"
//               >
//                 Simulate Failed API Call
//               </Button>
//               <Button
//                 onClick={async () => {
//                   try {
//                     await promiseToast(simulateAPICall(false, 3000), {
//                       loading: "Creating new account...",
//                       success: (data) => `Success! ${data.data}`,
//                       error: (err) => `Error: ${err.message}`,
//                     });
//                   } catch (error) {
//                     console.error(error);
//                   }
//                 }}
//                 className="w-full bg-blue-600 hover:bg-blue-700"
//               >
//                 Dynamic Success Message
//               </Button>
//               <Button
//                 onClick={async () => {
//                   try {
//                     await promiseToast(simulateAPICall(false, 1000), {
//                       loading: "Quick request...",
//                       success: "Done in 1 second!",
//                       error: "Something went wrong",
//                     });
//                   } catch (error) {
//                     console.error(error);
//                   }
//                 }}
//                 variant="secondary"
//                 className="w-full"
//               >
//                 Fast API Call (1s)
//               </Button>
//             </CardContent>
//           </Card>
//         </div>
//       </div>
//     </main>
//   );
// }
